import React, { useState, useEffect } from "react";
import LazyImage from "../ui/LazyImage";
import { X, ImageIcon, ShoppingCart, Eye, Inbox } from "lucide-react";

const Orders = ({ orders, colors }) => {
    const [localOrders, setLocalOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);

    useEffect(() => {
        // Hiện order đã success, hoặc order cũ chưa có status field (backward compat)
        // Không hiện order bị failed/cancelled
        setLocalOrders((orders || []).filter(o => {
            const s = o.leatherInfo?.status;
            return s === 'success' || s === 'purchased' || !s;
        }));
    }, [orders]);

    const cardStyle = {
        backgroundColor: colors.bgCard,
        border: `1px solid ${colors.borderColor}`,
        borderRadius: '20px'
    };

    const iconBoxStyle = (bgColor, color) => ({
        width: '50px', height: '50px',
        backgroundColor: bgColor, color: color,
        borderRadius: '16px', display: 'flex',
        justifyContent: 'center', alignItems: 'center'
    });

    const renderCoordBlock = (cutCoordinates) => {
        if (!cutCoordinates) return <span style={{ color: colors.textMuted }}>—</span>;

        if (cutCoordinates.isFreeMove) {
            const { short, long } = cutCoordinates;
            return (
                <div style={{ fontSize: '0.78rem', fontFamily: 'monospace', lineHeight: 1.6 }}>
                    <div style={{ marginBottom: '6px' }}>
                        <span style={{ color: '#f59e0b', fontWeight: 700, fontSize: '0.7rem', display: 'block' }}>SHORT STRAP</span>
                        <span style={{ color: colors.textMain }}>X: <b>{short?.xMm ?? '–'} mm</b>, Y: <b>{short?.yMm ?? '–'} mm</b></span><br />
                        <span style={{ color: colors.textMuted }}>{short?.widthMm ?? '–'} × {short?.heightMm ?? '–'} mm</span>
                    </div>
                    <div>
                        <span style={{ color: '#f59e0b', fontWeight: 700, fontSize: '0.7rem', display: 'block' }}>LONG STRAP</span>
                        <span style={{ color: colors.textMain }}>X: <b>{long?.xMm ?? '–'} mm</b>, Y: <b>{long?.yMm ?? '–'} mm</b></span><br />
                        <span style={{ color: colors.textMuted }}>{long?.widthMm ?? '–'} × {long?.heightMm ?? '–'} mm</span>
                    </div>
                </div>
            );
        }

        return (
            <div style={{ fontSize: '0.78rem', fontFamily: 'monospace', lineHeight: 1.6 }}>
                <span style={{ color: colors.textMain }}>X: <b>{cutCoordinates.xMm ?? '–'} mm</b>, Y: <b>{cutCoordinates.yMm ?? '–'} mm</b></span><br />
                <span style={{ color: colors.textMuted }}>
                    {cutCoordinates.widthMm ?? '–'} × {cutCoordinates.heightMm ?? '–'} mm
                </span>
            </div>
        );
    };

    // ── POPUP ────────────────────────────────────────────────────────────────
    const renderPopup = () => {
        if (!selectedOrder) return null;
        const o = selectedOrder;
        const cc = o.cutCoordinates;
        const cfg = o.strapConfig || {
            width: o.strapOptions?.['lugWidth']?.value ?? o.strapOptions?.['lugWidthMm']?.value,
            shortLength: o.strapOptions?.['shortLength']?.value ?? o.strapOptions?.['shortLengthMm']?.value,
            longLength: o.strapOptions?.['longLength']?.value ?? o.strapOptions?.['longLengthMm']?.value,
            buckleWidth: o.strapOptions?.['buckleWidth']?.value ?? o.strapOptions?.['buckleWidthMm']?.value
        };

        const mockupUrl = o.mockupImageUrl || o.mockupImage || '';
        const overviewUrl = o.overviewImageUrl || o.hideOverviewImage || '';

        const overlay = {
            position: 'fixed', inset: 0, zIndex: 9999,
            backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            padding: '16px'
        };

        const box = {
            backgroundColor: colors.bgCard,
            border: `1px solid ${colors.borderColor}`,
            borderRadius: '24px',
            width: '100%', maxWidth: '860px',
            maxHeight: '90vh', overflowY: 'auto',
            padding: '28px 32px',
            boxShadow: '0 24px 64px rgba(0,0,0,0.4)'
        };

        const sectionTitle = {
            color: '#f59e0b', fontWeight: 700, fontSize: '0.72rem',
            letterSpacing: '1.5px', textTransform: 'uppercase',
            marginBottom: '12px', paddingBottom: '6px',
            borderBottom: `1px solid ${colors.borderColor}`
        };

        const labelStyle = { color: colors.textMuted, fontSize: '0.8rem', minWidth: '130px' };
        const valueStyle = { color: colors.textMain, fontSize: '0.9rem', fontWeight: 600 };

        const renderInfoRow = (label, value) => (
            <div className="d-flex align-items-start" style={{ marginBottom: '10px' }}>
                <span style={labelStyle}>{label}</span>
                <span style={valueStyle}>{value ?? '—'}</span>
            </div>
        );

        const renderCoordsDetail = () => {
            if (!cc) return <span style={{ color: colors.textMuted }}>No coordinate data</span>;

            const coordBox = (title, data) => (
                <div style={{ flex: 1, backgroundColor: colors.bgCard === '#ffffff' ? '#f8f7f5' : 'rgba(255,255,255,0.04)', borderRadius: '12px', padding: '14px 16px', border: `1px solid ${colors.borderColor}` }}>
                    <div style={{ color: '#f59e0b', fontWeight: 700, fontSize: '0.7rem', letterSpacing: '1px', marginBottom: '10px' }}>{title}</div>
                    {[
                        ['Position X', `${data?.xMm ?? '–'} mm`],
                        ['Position Y', `${data?.yMm ?? '–'} mm`],
                        ['Width', `${data?.widthMm ?? data?.width ?? '–'} mm`],
                        ['Height', `${data?.heightMm ?? data?.height ?? '–'} mm`],
                    ].map(([l, v]) => (
                        <div key={l} className="d-flex justify-content-between" style={{ marginBottom: '6px' }}>
                            <span style={{ color: colors.textMuted, fontSize: '0.8rem' }}>{l}</span>
                            <span style={{ color: colors.textMain, fontFamily: 'monospace', fontWeight: 600 }}>{v}</span>
                        </div>
                    ))}
                </div>
            );

            if (cc.isFreeMove) {
                return (
                    <div className="d-flex gap-3 flex-wrap">
                        {coordBox('SHORT STRAP', cc.short)}
                        {coordBox('LONG STRAP', cc.long)}
                    </div>
                );
            }
            return coordBox('PRE-SELECTED SLOT', cc);
        };

        return (
            <div style={overlay} onClick={() => setSelectedOrder(null)}>
                <div style={box} onClick={e => e.stopPropagation()}>

                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-start mb-4">
                        <div>
                            <h5 style={{ color: colors.textMain, fontWeight: 700, marginBottom: '4px' }}>Order Details</h5>
                            <span style={{ color: colors.textMuted, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                ID: {o.uniqueAreaId || o._id || '—'}
                            </span>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <span style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                                {o.createdAt ? new Date(o.createdAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                            </span>
                            <button
                                onClick={() => setSelectedOrder(null)}
                                style={{ background: 'none', border: `1px solid ${colors.borderColor}`, color: colors.textMain, borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>

                    {/* Hai ảnh */}
                    <div style={sectionTitle}>Images</div>
                    <div className="d-flex gap-3 mb-4 flex-wrap">
                        <div style={{ flex: 1, minWidth: '240px' }}>
                            <div style={{ color: colors.textMuted, fontSize: '0.75rem', fontWeight: 600, marginBottom: '8px', letterSpacing: '0.5px' }}>STRAP MOCKUP</div>
                            {mockupUrl ? (
                                <a href={mockupUrl} target="_blank" rel="noopener noreferrer">
                                    <LazyImage src={mockupUrl} alt="Strap Mockup"
                                        style={{ width: '100%', borderRadius: '12px', objectFit: 'contain', backgroundColor: '#F6F5F0', padding: '12px', border: `1px solid ${colors.borderColor}`, maxHeight: '280px' }} />
                                </a>
                            ) : (
                                <div style={{ height: '160px', borderRadius: '12px', border: `1px dashed ${colors.borderColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.textMuted, fontSize: '0.8rem' }}>
                                    No mockup image
                                </div>
                            )}
                        </div>
                        <div style={{ flex: 1, minWidth: '240px' }}>
                            <div style={{ color: colors.textMuted, fontSize: '0.75rem', fontWeight: 600, marginBottom: '8px', letterSpacing: '0.5px' }}>LEATHER HIDE OVERVIEW (cut removed)</div>
                            {overviewUrl ? (
                                <a href={overviewUrl} target="_blank" rel="noopener noreferrer">
                                    <LazyImage src={overviewUrl} alt="Hide Overview"
                                        style={{ width: '100%', borderRadius: '12px', objectFit: 'contain', backgroundColor: '#222', padding: '8px', border: `1px solid ${colors.borderColor}`, maxHeight: '280px' }} />
                                </a>
                            ) : (
                                <div style={{ height: '160px', borderRadius: '12px', border: `1px dashed ${colors.borderColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.textMuted, fontSize: '0.8rem', flexDirection: 'column', gap: '6px' }}>
                                    <ImageIcon size={24} />
                                    Not available for older orders
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Thông tin đơn */}
                    <div style={sectionTitle}>Order Information</div>
                    <div className="mb-4">
                        {renderInfoRow('Leather', o.leatherInfo?.name)}
                        {renderInfoRow('Price', `$${o.leatherInfo?.price ?? 0}`)}
                        {renderInfoRow('Selection Type', o.selectionType)}
                        {o.strapOptions && Object.keys(o.strapOptions).length > 0 ? (
                            Object.entries(o.strapOptions).map(([key, opt]) => {
                                // Bỏ qua hiển thị tên da, kiểu cắt, areaId vì chúng đã có ở trên
                                if (['leatherName', 'cutType', 'areaId'].includes(key)) return null;
                                return renderInfoRow(opt.label || key, opt.valueLabel || opt.value);
                            })
                        ) : (
                            cfg && cfg.width && <>
                                {renderInfoRow('Lug Width', `${cfg.width} mm`)}
                                {renderInfoRow('Short Length', `${cfg.shortLength} mm`)}
                                {renderInfoRow('Long Length', `${cfg.longLength} mm`)}
                            </>
                        )}
                    </div>

                    {/* Customer Info */}
                    {o.customerInfo && (o.customerInfo.name || o.customerInfo.email) && (
                        <>
                            <div style={sectionTitle}>Customer Info</div>
                            <div className="mb-4">
                                {o.customerInfo.name && renderInfoRow('Name', o.customerInfo.name)}
                                {o.customerInfo.email && renderInfoRow('Email', o.customerInfo.email)}
                                {o.customerInfo.phone && renderInfoRow('Phone', o.customerInfo.phone)}
                                {o.customerInfo.wcOrderNumber && renderInfoRow('WC Order #', `#${o.customerInfo.wcOrderNumber}`)}
                                {o.customerInfo.wcOrderId && renderInfoRow('WC Order ID', o.customerInfo.wcOrderId)}
                            </div>
                        </>
                    )}

                    {/* Cut Coordinates */}
                    <div style={sectionTitle}>Cut Coordinates (mm)</div>
                    {renderCoordsDetail()}
                </div>
            </div>
        );
    };

    return (
        <div className="animate-fade-in">
            {renderPopup()}

            <div className="mb-4">
                <h3 className="fw-bold mb-1" style={{ color: colors.textMain, letterSpacing: '0.5px' }}>Customer Designs</h3>
                <span style={{ color: colors.textMuted, fontSize: '14px' }}>Purchased orders — click a row to view full details</span>
            </div>

            <div className="p-4 shadow-sm hover-card" style={cardStyle}>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h6 className="fw-bold m-0 d-flex align-items-center" style={{ fontSize: '15px', color: colors.textMain }}>
                        <div className="me-3" style={iconBoxStyle('rgba(34, 197, 94, 0.1)', '#22c55e')}>
                            <ShoppingCart size={16} />
                        </div>
                        Purchased Designs History
                    </h6>
                    <span style={{ backgroundColor: 'rgba(34,197,94,0.1)', color: '#22c55e', borderRadius: '20px', padding: '4px 14px', fontSize: '0.8rem', fontWeight: 600 }}>
                        {localOrders.length} orders
                    </span>
                </div>

                <div className="table-responsive">
                    <table className="table table-borderless align-middle" style={{ color: colors.textMain }}>
                        <thead>
                            <tr className="small text-uppercase" style={{ color: colors.textMuted, borderBottom: `1px solid ${colors.borderColor}` }}>
                                <th className="pb-3 fw-bold ps-3">Mockup</th>
                                <th className="pb-3 fw-bold">Leather Source</th>
                                <th className="pb-3 fw-bold">Customer</th>
                                <th className="pb-3 fw-bold text-center">Selection</th>
                                <th className="pb-3 fw-bold text-center">Cut Position (mm)</th>
                                <th className="pb-3 fw-bold text-center">Details</th>
                                <th className="pb-3 fw-bold text-end pe-3">Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {localOrders.length > 0 ? localOrders.map((order, index) => {
                                return (
                                <tr
                                    key={order._id || index}
                                    onClick={() => setSelectedOrder(order)}
                                    style={{
                                        borderBottom: index === localOrders.length - 1 ? 'none' : `1px solid ${colors.borderColor}`,
                                        cursor: 'pointer',
                                        transition: 'background 0.15s'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    <td className="py-3 ps-3">
                                        {(() => { const mu = order.mockupImageUrl || order.mockupImage || ''; return mu ? (
                                            <LazyImage src={mu} alt="mockup"
                                                style={{ height: '56px', width: 'auto', objectFit: 'contain', borderRadius: '6px', backgroundColor: '#F6F5F0', padding: '3px', border: '1px solid #E9E4DB' }} />
                                        ) : (
                                            <div style={{ width: '40px', height: '56px', borderRadius: '6px', border: '1px dashed #E9E4DB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <ImageIcon size={14} color="#ccc" />
                                            </div>
                                        ); })()}
                                    </td>
                                    <td className="py-3 fw-bold">
                                        {order.leatherInfo?.name || "Unknown Leather"}<br />
                                        <span className="text-success fw-bold small">${order.leatherInfo?.price || 0}</span>
                                    </td>
                                    <td className="py-3">
                                        {order.customerInfo?.name ? (
                                            <>
                                                <span style={{ fontWeight: 600, fontSize: '0.88rem', color: colors.textMain }}>{order.customerInfo.name}</span><br />
                                                {order.customerInfo.email && <span style={{ fontSize: '0.75rem', color: colors.textMuted }}>{order.customerInfo.email}</span>}
                                            </>
                                        ) : (
                                            <span style={{ color: colors.textMuted, fontSize: '0.78rem' }}>Pending</span>
                                        )}
                                    </td>
                                    <td className="py-3 text-center">
                                        <span style={{ fontSize: '0.78rem', fontWeight: 600, padding: '3px 10px', borderRadius: '20px', backgroundColor: order.cutCoordinates?.isFreeMove ? 'rgba(139,92,246,0.15)' : 'rgba(245,158,11,0.15)', color: order.cutCoordinates?.isFreeMove ? '#a78bfa' : '#f59e0b' }}>
                                            {order.cutCoordinates?.isFreeMove ? 'Free Move' : 'Pre-selected'}
                                        </span>
                                    </td>
                                    <td className="py-3 text-center">
                                        {renderCoordBlock(order.cutCoordinates)}
                                    </td>
                                    <td className="py-3 text-center">
                                        <button
                                            onClick={e => { e.stopPropagation(); setSelectedOrder(order); }}
                                            style={{ background: 'none', border: `1px solid ${colors.borderColor}`, color: colors.textMain, borderRadius: '8px', padding: '5px 12px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                                        >
                                            <Eye size={14} /> View
                                        </button>
                                    </td>
                                    <td className="py-3 text-end small pe-3" style={{ color: colors.textMuted, fontWeight: '500' }}>
                                        {new Date(order.createdAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                                    </td>
                                </tr>
                            );}) : (
                                <tr>
                                    <td colSpan="7" className="text-center py-5" style={{ color: colors.textMuted }}>
                                        <Inbox size={32} style={{ display: 'block', marginBottom: '8px' }} />
                                        No purchased designs yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Orders;
